import { Button } from "@/components/ui/button";
import facility from "@/assets/knmc-facilities.jpeg";
import { ArrowRight } from "lucide-react";
import { Reveal } from "./Reveal";
import type { FacilitiesSettings as FacilitiesSettingsData } from "@/hooks/useHomeSettings";

const tags = [
  "ICU",
  "NICU",
  "HDU",
  "Operation Theatre",
  "Emergency Dept.",
  "Laboratory",
  "Radiology",
  "Pharmacy",
];

export function Facilities({ settings }: { settings?: FacilitiesSettingsData | null }) {
  const heading = settings?.heading || "Technology & Facilities";
  const title = settings?.title || "Built for precision. Designed for healing.";
  const description =
    settings?.description ||
    "From state-of-the-art operating theatres to advanced imaging and round-the-clock critical care units \u2014 every facility at KNMC meets international clinical standards.";
  const ctaLabel = settings?.cta_label || "Take a virtual tour";

  return (
    <section className="py-16 md:py-24 overflow-hidden">
      <div className="mx-auto grid max-w-7xl gap-10 px-6 lg:grid-cols-12 lg:items-center lg:gap-12">
        {/* Left Column: Image with Compact Floating Badge */}
        <Reveal className="lg:col-span-6">
          <div className="relative">
            {/* Image Container */}
            <div className="hover-zoom-img aspect-[4/3] w-full overflow-hidden rounded-2xl md:rounded-3xl shadow-elevated">
              <img
                src={facility}
                alt="Modern operating theatre with advanced equipment"
                loading="lazy"
                width={1280}
                height={896}
                className="h-full w-full object-cover"
              />
            </div>

            {/* Compact Floating Badge (Bottom Right) */}
            <div className="absolute -bottom-4 -right-2 z-10 hidden sm:flex items-center gap-3.5 rounded-2xl border border-border bg-card px-5 py-3.5 shadow-xl md:-bottom-6 md:-right-4">
              <div className="font-display text-2xl font-bold leading-none text-primary">
                A+
              </div>
              <div className="h-7 w-px bg-border" />
              <div className="text-[11px] font-semibold uppercase tracking-wider leading-tight text-muted-foreground">
                Infection
                <br />
                Control
              </div>
            </div>
          </div>
        </Reveal>

        {/* Right Column: Text Content */}
        <Reveal delay={120} className="flex flex-col items-start lg:col-span-6">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-secondary md:text-sm">
            {heading}
          </p>
          <h2 className="font-display text-2xl font-bold tracking-tight text-primary sm:text-3xl md:text-4xl lg:text-5xl leading-tight">
            {title}
          </h2>
          <p className="mt-4 text-sm text-muted-foreground sm:text-base leading-relaxed">
            {description}
          </p>

          {/* Tags */}
          <div className="mt-6 flex flex-wrap items-center gap-2 md:mt-8">
            {tags.map((t) => (
              <span
                key={t}
                className="inline-flex h-fit items-center rounded-full border border-primary/20 bg-primary-soft px-3.5 py-1.5 text-xs font-medium text-primary sm:px-4 sm:py-2 sm:text-sm"
              >
                {t}
              </span>
            ))}
          </div>

          {/* CTA Button */}
          {/* <Button className="mt-8 w-full rounded-full bg-primary px-6 py-6 text-sm font-semibold text-primary-foreground hover:bg-secondary sm:w-auto md:mt-10">
            {ctaLabel} <ArrowRight className="ml-2 h-4 w-4" />
          </Button> */}
        </Reveal>
      </div>
    </section>
  );
}